import type { IMfaEnrollView, IMfaPasskeyOptionsView, ILoginSuccessView, MfaMethod } from '@shared/services';
import { publicApi } from '../../client';

/**
 * Accounts-unlocked REST view: the response an identity-scoped MFA ceremony returns instead of a
 * session — `{ outcome: 'accounts', accountsProof }`. Defined locally (not in `@shared/services`)
 * because it is the REST/camelCase view of the `outcome: 'accounts'` outcome, mirroring how
 * `login.request.ts` defines its own local challenge views.
 */
export interface IAccountsUnlockedView {
  outcome: 'accounts';
  accountsProof: string;
}

/**
 * The dual outcome of an MFA verify (TOTP/backup/email code or passkey assertion). The SAME
 * `/auth/mfa/*` endpoints answer:
 *
 * - `ILoginSuccessView` (`{ access_token }`) when driven by an ACCOUNT-scoped `mfaPending` — the
 *   retained `login`/switch fallback — which the caller adopts as a session, as before.
 * - `IAccountsUnlockedView` (`{ outcome: 'accounts', accountsProof }`) when driven by an
 *   IDENTITY-scoped `mfaPending` — the MFA-first `check-credentials` path — which carries no session;
 *   the caller captures `accountsProof` and continues the login flow.
 *
 * The request functions surface both shapes; the caller discriminates with `isAccountsProof`.
 */
export type MfaVerifyResponse = ILoginSuccessView | IAccountsUnlockedView;

/** Discriminates an accounts-proof (identity-scoped) from a session (account-scoped) verify result. */
export const isAccountsProof = (response: MfaVerifyResponse): response is IAccountsUnlockedView =>
  'outcome' in response && response.outcome === 'accounts';

/**
 * Login-time MFA ceremony requests. Every `/auth/mfa/*` endpoint is authorised by the short-lived
 * `mfaPending` bearer token issued with the MFA challenge, sent as `Authorization: Bearer
 * <mfaPending>` and reused unchanged across a ceremony's steps (enroll→confirm, options→verify) —
 * cloned from the `changePasswordRequest` pattern. On success the gateway sets the refresh cookie
 * and returns a snake_case `{ access_token }` view, exactly like normal login.
 */
const bearer = (mfaPending: string) => ({ headers: { Authorization: `Bearer ${mfaPending}` } });

/**
 * Confirm-enrollment REST view: the gateway returns a login success payload (`access_token`) and,
 * for TOTP only, the one-time `backupCodes`. Email/passkey enrollment return no codes. When enrollment
 * was driven by an identity-scoped `mfaPending` (the MFA-first path), confirm additionally carries an
 * `accountsProof`, which the login wizard captures to continue the flow.
 */
export interface IMfaConfirmRestView extends ILoginSuccessView {
  backupCodes?: string[];
  accountsProof?: string;
}

export interface MfaEnrollParams {
  mfaPending: string;
  method: MfaMethod;
}

export interface MfaCodeParams {
  mfaPending: string;
  code: string;
}

export interface MfaPendingParams {
  mfaPending: string;
}

export interface MfaPasskeyVerifyParams {
  mfaPending: string;
  response: string;
  /** Optional human label for a REGISTRATION ceremony so a person can tell their passkeys apart;
   * ignored by the authentication ceremony. */
  label?: string;
}

/** Start enrollment for a method. TOTP returns `{ otpauthUri, secret }`; email sends a code. */
export async function mfaEnrollRequest({ mfaPending, method }: MfaEnrollParams): Promise<IMfaEnrollView> {
  const { data } = await publicApi.post('/api/v1/auth/mfa/enroll', { method }, bearer(mfaPending));

  return data;
}

/** Confirm enrollment with the setup code. TOTP additionally returns the one-time `backupCodes`. */
export async function mfaConfirmRequest({ mfaPending, code }: MfaCodeParams): Promise<IMfaConfirmRestView> {
  const { data } = await publicApi.post('/api/v1/auth/mfa/confirm', { code }, bearer(mfaPending));

  return data;
}

/** Verify a login challenge with a TOTP, backup, or emailed code. */
export async function mfaVerifyRequest({ mfaPending, code }: MfaCodeParams): Promise<MfaVerifyResponse> {
  const { data } = await publicApi.post('/api/v1/auth/mfa/verify', { code }, bearer(mfaPending));

  return data;
}

/** Send a one-time code to the account's email for the verification flow. */
export async function mfaRequestEmailCodeRequest({ mfaPending }: MfaPendingParams): Promise<void> {
  await publicApi.post('/api/v1/auth/mfa/email/request', {}, bearer(mfaPending));
}

/** Start a passkey registration ceremony; returns WebAuthn options as a JSON string. */
export async function mfaPasskeyRegisterOptionsRequest({
  mfaPending,
}: MfaPendingParams): Promise<IMfaPasskeyOptionsView> {
  const { data } = await publicApi.post('/api/v1/auth/mfa/passkey/register/options', {}, bearer(mfaPending));

  return data;
}

/** Complete a passkey registration ceremony with the browser's stringified WebAuthn response. */
export async function mfaPasskeyRegisterVerifyRequest({
  mfaPending,
  response,
  label,
}: MfaPasskeyVerifyParams): Promise<MfaVerifyResponse> {
  // Omit `label` when unset so the backend keeps its null-label default rather than storing an empty string.
  const { data } = await publicApi.post(
    '/api/v1/auth/mfa/passkey/register/verify',
    { response, ...(label ? { label } : {}) },
    bearer(mfaPending),
  );

  return data;
}

/** Start a passkey authentication ceremony; returns WebAuthn options as a JSON string. */
export async function mfaPasskeyAuthOptionsRequest({ mfaPending }: MfaPendingParams): Promise<IMfaPasskeyOptionsView> {
  const { data } = await publicApi.post('/api/v1/auth/mfa/passkey/auth/options', {}, bearer(mfaPending));

  return data;
}

/** Complete a passkey authentication ceremony with the browser's stringified WebAuthn response. */
export async function mfaPasskeyAuthVerifyRequest({
  mfaPending,
  response,
}: MfaPasskeyVerifyParams): Promise<MfaVerifyResponse> {
  const { data } = await publicApi.post('/api/v1/auth/mfa/passkey/auth/verify', { response }, bearer(mfaPending));

  return data;
}
